import { createContext, PropsWithChildren, useState, useContext } from 'react';
import * as React from 'react';

/** Any modals to be used within the app should be defined here */
export enum AppModals {
  NONE,
  PROFILE_ADDRESS,
  PROFILE_PHONE_NUMBER,
  PROFILE_PASSPORT,
  PROFILE_EMERGENCY_CONTACT,
  DELETE_EMERGENCY_CONTACT,
  PROFILE_LOYALTY_CARD,
  DELETE_LOYALTY,
  PROFILE_PAYMENT_CARD,
  DELETE_PAYMENT,
  UNLOAD_GUARD,
  PROFILE_PICTURE,
  COMPANY_PICTURE,
  UNMASK_CONFIRMATION,
  UNMASK_TMC_CONFIRMATION,
  COUNTRY_CODE,
  AIR_FARE_RULES,
  CUSTOM_POLICY_REASON_CODES,
  LOYALTY,
  TRAVEL_DOCS,
}

type ModalName = AppModals | null;

interface IModalContext {
  modalName: ModalName;
  showModal: (data: AppModals) => void;
  hideModal: () => void;
}

const ModalContext = createContext<IModalContext | undefined>(undefined);

// As of now, ModalProvider only supports storing data about 1 modal at a time.
// Later this can be extended to support multiple modals by using an array/set
export const ModalProvider: React.FC<PropsWithChildren<unknown>> = ({ children }: PropsWithChildren<unknown>) => {
  const [modalName, setModalName] = useState<ModalName>(null);

  const showModal = (name: AppModals): void => {
    setModalName(name);
  };

  const hideModal = (): void => {
    setModalName(null);
  };

  const values = {
    modalName,
    showModal,
    hideModal,
  };

  return <ModalContext.Provider value={values}>{children}</ModalContext.Provider>;
};

export const useModalContext = (): IModalContext => {
  const context = useContext(ModalContext);

  if (context === undefined) {
    throw new Error('`useModalContext` hook must be used within a `ModalProvider` component');
  }

  return context;
};
